import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { TabView, TabPanel } from 'primereact/tabview';
import './RepairOrderDetail.scss';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputTextarea } from 'primereact/inputtextarea';
import { InputNumber } from 'primereact/inputnumber';
import { handleGetRequest } from '../../service/GetTemplate';
import { handlePatchRequest } from '../../service/PatchTemplate';
import { handlePostRequest } from '../../service/PostTemplate';
import { endPoints } from '../../Config';
import SupportHandoffButton from '../../components/SupportHandoffButton';
import PhotoUploader from '../../components/PhotoUploader';
import { formatLocalizedDateTime, formatTimeFromMinutes } from '../../utilities/formatTime';
import { getPreferredLanguage } from '../../utilities/languagePreference';
import { translateUiText, translateUiToken } from '../../utilities/siteTranslations';
import {
    getAllFailureTypeOptions,
    getFailureCategoryOptions,
    getFailureCategoryPriority,
    getFailureTypeOptionsForMachineType,
    inferRepairMachineType,
} from '../../utilities/repairOrderFailureOptions';

const FINAL_STATUS_OPTIONS = [
    { label: 'Fully Operational', value: 'Fully Operational' },
    {
        label: 'Operational with minor issue',
        value: 'Operational with minor issue',
    },
    { label: 'Requires Follow-up', value: 'Requires Follow-up' },
];

const MAX_CLOSE_PHOTOS = 10;
const MAX_CLOSE_TOTAL_BYTES = 5 * 1024 * 1024;
const QR_REPAIR_ISSUE_SUMMARY = 'Machine issue reported via QR';
const FOLLOW_UP_ORDER_TYPE = 'follow_up';
const LEGACY_INTERNAL_CHAT_PATTERNS = [/^\[system\]/i, /^Complaint opened for /i, /^Selected Level [123]:/i];

const isInternalChatEntry = (entry) => {
    if (!entry) return true;
    if (typeof entry.internal === 'boolean') return entry.internal;
    return LEGACY_INTERNAL_CHAT_PATTERNS.some((pattern) => pattern.test(String(entry.text || '')));
};

const RepairOrderDetail = () => {
    const { id } = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [preferredLanguage, setPreferredLanguage] = useState(getPreferredLanguage());

    const [editVisible, setEditVisible] = useState(false);
    const [savingEdit, setSavingEdit] = useState(false);
    const [processingEditPhoto, setProcessingEditPhoto] = useState(false);
    const [editForm, setEditForm] = useState({ issueSummary: '', photoUrl: '' });
    const [activeTab, setActiveTab] = useState(0);
    const [closeVisible, setCloseVisible] = useState(false);
    const [requestVisible, setRequestVisible] = useState(false);
    const [loadingOrder, setLoadingOrder] = useState(true);
    const [orderLoadFailed, setOrderLoadFailed] = useState(false);
    const [order, setOrder] = useState(null);
    const [complaint, setComplaint] = useState(null);
    const [spareParts, setSpareParts] = useState([]);
    const [sparePartUsage, setSparePartUsage] = useState([]);
    const [closeForm, setCloseForm] = useState({
        typeOfFailure: '',
        failureCategory: '',
        actionsTaken: '',
        finalStatus: '',
        photoUrls: [],
    });
    const [requestForm, setRequestForm] = useState({
        sparePartId: '',
        quantity: 1,
        notes: '',
    });
    const [requestPartCategory, setRequestPartCategory] = useState(null);
    const [savingClose, setSavingClose] = useState(false);
    const [savingRequest, setSavingRequest] = useState(false);
    const [photoPreviewUrl, setPhotoPreviewUrl] = useState('');
    const [photoPreviewVisible, setPhotoPreviewVisible] = useState(false);
    const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
    const user = JSON.parse(localStorage.getItem('userDetails') || '{}');
    const roleUid = user?.data?.adminUser?.role?.uid;
    const isOperator = roleUid === 'operator';
    const t = useCallback((value) => translateUiText(value, preferredLanguage), [preferredLanguage]);

    const formatUserDisplay = (userData) => {
        if (!userData) return '-';
        const fullName = `${userData.firstName || ''} ${userData.lastName || ''}`.trim();
        if (fullName) return fullName;
        return userData.email || '-';
    };

    const loadOrder = async () => {
        setLoadingOrder(true);
        setOrderLoadFailed(false);
        const response = await handleGetRequest(endPoints.getRepairOrderById(id), true, false, dispatch);
        setLoadingOrder(false);
        setOrderLoadFailed(!response?.data?._id);
        if (response?.data) {
            setOrder(response.data);
            const existingPhotos = Array.isArray(response.data.photos) ? response.data.photos.filter((photo) => typeof photo === 'string' && photo.trim()) : [];
            setCloseForm({
                typeOfFailure: response.data.typeOfFailure || '',
                failureCategory: response.data.failureCategory || '',
                actionsTaken: response.data.actionsTaken || '',
                finalStatus: response.data.finalStatus || '',
                photoUrls: existingPhotos,
            });
            // Always load complaint details separately to ensure all fields are populated (including questions)
            if (response.data.complaint) {
                const complaintId = response.data.complaint._id || response.data.complaint;
                if (complaintId) {
                    await loadComplaint(complaintId);
                }
            }
        }
    };

    const loadComplaint = async (complaintId) => {
        if (!complaintId) return;
        const response = await handleGetRequest(endPoints.getComplaintById(complaintId), true, false, dispatch);
        // Handle different response structures: response.data.data (wrapped) or response.data (direct)
        const complaintData = response?.data?.data || response?.data;
        if (complaintData) {
            setComplaint(complaintData);
        }
    };

    const loadSpareParts = async () => {
        const response = await handleGetRequest(endPoints.getallPartsInventory, true, false, dispatch);
        if (response?.data) {
            setSpareParts(response.data);
        }
    };

    const loadSparePartUsage = async (complaintId) => {
        if (!complaintId) return;
        const response = await handleGetRequest(endPoints.getSparePartUsageByComplaint(complaintId), true, false, dispatch);
        if (response?.data) {
            setSparePartUsage(response.data);
        }
    };

    useEffect(() => {
        loadOrder();
        loadSpareParts();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    useEffect(() => {
        const complaintId = complaint?._id || order?.complaint?._id || order?.complaint;
        if (complaintId) {
            loadSparePartUsage(complaintId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [complaint?._id, order?.complaint?._id, order?.complaint]);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        const syncPreferredLanguage = () => setPreferredLanguage(getPreferredLanguage());
        window.addEventListener('preferred-language-changed', syncPreferredLanguage);
        window.addEventListener('storage', syncPreferredLanguage);

        return () => {
            window.removeEventListener('preferred-language-changed', syncPreferredLanguage);
            window.removeEventListener('storage', syncPreferredLanguage);
        };
    }, []);

    const handleCloseOrder = async () => {
        if (!order?._id) return;
        setSavingClose(true);
        const photoUrls = (closeForm.photoUrls || []).filter((photo) => typeof photo === 'string' && photo.trim());
        if (photoUrls.length > MAX_CLOSE_PHOTOS) {
            toast.warn(t(`Maximum ${MAX_CLOSE_PHOTOS} photos allowed.`));
            setSavingClose(false);
            return;
        }
        const isFollowUp = order?.orderType === FOLLOW_UP_ORDER_TYPE;
        const payload = {
            actionsTaken: closeForm.actionsTaken,
            finalStatus: closeForm.finalStatus,
            photoUrls,
            // Backward compatibility for any endpoint reading single photo.
            photoUrl: photoUrls[0],
        };
        if (!isFollowUp) {
            payload.typeOfFailure = closeForm.typeOfFailure;
            payload.failureCategory = closeForm.failureCategory;
        }
        const response = await dispatch(handlePostRequest(payload, endPoints.closeRepairOrder(order._id), true));
        setSavingClose(false);
        if (response?.status === 200) {
            setCloseVisible(false);
            await loadOrder();
        }
    };

    const handleRequestSparePart = async () => {
        if (!order?.machine?._id || !requestForm.sparePartId || !requestForm.quantity) return;
        setSavingRequest(true);
        const user = JSON.parse(localStorage.getItem('userDetails') || '{}');
        const currentUserId = user?.data?.adminUser?._id;
        const technicianId = order?.technician?._id || currentUserId;
        const payload = {
            sparePartId: requestForm.sparePartId,
            machineId: order.machine._id,
            repairOrderId: order._id,
            quantity: requestForm.quantity,
            usageType: 'repair',
            notes: requestForm.notes,
            technicianId,
        };
        const response = await dispatch(handlePostRequest(payload, endPoints.requestSparePartUsage, true));
        setSavingRequest(false);
        if (response?.status === 201) {
            setRequestVisible(false);
            setActiveTab(2);
            setRequestForm({
                sparePartId: '',
                quantity: 1,
                notes: '',
            });
            setRequestPartCategory(null);
            const complaintId = complaint?._id || order?.complaint?._id || order?.complaint;
            if (complaintId) {
                await loadSparePartUsage(complaintId);
            }
        }
    };

    const currentUserId = user?.data?.adminUser?._id;
    const creatorId = order?.createdBy?._id || order?.createdBy;
    const isAdmin = ['admin', 'superAdmin'].includes(roleUid);
    const canEditReport = Boolean(order && currentUserId && (isAdmin || creatorId === currentUserId) && !order.isArchived);
    const handleSaveReport = async () => {
        if (!canEditReport || savingEdit || processingEditPhoto) return;
        const payload = {};
        if (editForm.issueSummary.trim()) payload.issueSummary = editForm.issueSummary.trim();
        if (editForm.photoUrl) payload.photoUrl = editForm.photoUrl;
        if (!Object.keys(payload).length) return;
        setSavingEdit(true);
        try {
            const response = await dispatch(handlePatchRequest(payload, `${endPoints.getRepairOrderById(id)}/report`, true));
            if (response?.status === 200) {
                setEditVisible(false);
                await loadOrder();
            }
        } finally {
            setSavingEdit(false);
        }
    };

    const canClose = order && order.status === 'open' && !order.isArchived && !isOperator;
    const isFollowUpOrder = order?.orderType === FOLLOW_UP_ORDER_TYPE;
    const hasAtLeastOnePhoto = Array.isArray(closeForm.photoUrls) && closeForm.photoUrls.length > 0;
    const missingRepairFields = !isFollowUpOrder && (!closeForm.typeOfFailure || !closeForm.failureCategory);
    const closeDisabled = missingRepairFields || !closeForm.actionsTaken.trim() || !closeForm.finalStatus || !hasAtLeastOnePhoto || !canClose || savingClose;
    const repairMachineType =
        order?.repairMachineType ||
        complaint?.manualIntake?.repairMachineType ||
        order?.complaint?.manualIntake?.repairMachineType ||
        inferRepairMachineType([order?.machine?.machine, order?.machine?.type, order?.machine?.name, order?.machine?.model].filter(Boolean).join(' '));

    const localizedFailureTypeOptions = useMemo(() => {
        const options = repairMachineType ? getFailureTypeOptionsForMachineType(repairMachineType) : getAllFailureTypeOptions();
        if (closeForm.typeOfFailure && !options.some((item) => item.value === closeForm.typeOfFailure)) {
            options.push({
                label: closeForm.typeOfFailure,
                value: closeForm.typeOfFailure,
            });
        }
        return options.map((item) => ({ ...item, label: t(item.label) }));
    }, [t, repairMachineType, closeForm.typeOfFailure]);
    const localizedFinalStatusOptions = useMemo(() => FINAL_STATUS_OPTIONS.map((item) => ({ ...item, label: t(item.label) })), [t]);
    const localizedFailureCategoryOptions = useMemo(() => {
        const options = getFailureCategoryOptions(repairMachineType, closeForm.typeOfFailure);
        if (closeForm.failureCategory && !options.some((item) => item.value === closeForm.failureCategory)) {
            options.push({
                label: closeForm.failureCategory,
                value: closeForm.failureCategory,
            });
        }
        return options.map((item) => ({ ...item, label: t(item.label) }));
    }, [t, repairMachineType, closeForm.typeOfFailure, closeForm.failureCategory]);
    const selectedFailurePriority = getFailureCategoryPriority(closeForm.failureCategory);

    const sparePartCategoryOptions = useMemo(() => {
        const categories = [
            ...new Set(
                spareParts
                    .filter((part) => part?.isActive !== false && ['New', 'Ready to Install'].includes(part?.condition) && Number(part?.quantityInStock || 0) > 0)
                    .map((part) => part?.category)
                    .filter(Boolean)
            ),
        ];
        return categories
            .sort((a, b) => a.localeCompare(b))
            .map((category) => ({
                label: t(category),
                value: category,
            }));
    }, [t, spareParts]);

    const sparePartOptions = useMemo(() => {
        return spareParts
            .filter(
                (part) =>
                    part?.isActive !== false &&
                    ['New', 'Ready to Install'].includes(part?.condition) &&
                    Number(part?.quantityInStock || 0) > 0 &&
                    (!requestPartCategory || part?.category === requestPartCategory)
            )
            .map((part) => ({
                label: `${part.sku} – ${part.description} (${part.quantityInStock})`,
                value: part._id,
            }));
    }, [requestPartCategory, spareParts]);

    const partsUsedRows =
        order?.partsUsed && Array.isArray(order.partsUsed)
            ? order.partsUsed.map((p, idx) => ({
                  id: idx,
                  sku: p.usage?.sparePart?.sku || '',
                  description: p.usage?.sparePart?.description || '',
                  quantity: p.usage?.quantity || '',
                  technician:
                      p.usage?.technician?.firstName || p.usage?.technician?.lastName ? `${p.usage.technician.firstName || ''} ${p.usage.technician.lastName || ''}`.trim() : '',
              }))
            : [];

    const assignmentLogRows = Array.isArray(order?.assignmentLogs)
        ? [...order.assignmentLogs]
              .sort((a, b) => new Date(b?.claimedAt || 0) - new Date(a?.claimedAt || 0))
              .map((entry, index) => ({
                  id: index,
                  technician: formatUserDisplay(entry?.technician),
                  claimedBy: entry?.claimedBy ? formatUserDisplay(entry.claimedBy) : t('System'),
                  claimedAt: formatLocalizedDateTime(entry?.claimedAt, preferredLanguage),
                  source: entry?.source ? t(entry.source) : 'manual',
              }))
        : [];

    const statusTag = (status) => {
        if (!status) return <span className="text-500">-</span>;
        const map = {
            open: {
                label: translateUiToken('open', preferredLanguage),
                color: '#854d0e',
                bg: '#fef9c3',
            },
            completed: {
                label: translateUiToken('completed', preferredLanguage),
                color: '#16a34a',
                bg: 'rgba(22,163,74,0.1)',
            },
        };
        const item = map[status] || {
            label: t(status),
            color: '#64748b',
            bg: 'rgba(148,163,184,0.15)',
        };
        return (
            <span
                style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: '0.15rem 0.6rem',
                    borderRadius: '999px',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    color: item.color,
                    background: item.bg,
                    textTransform: 'uppercase',
                    letterSpacing: '0.03em',
                }}
            >
                {item.label}
            </span>
        );
    };

    const getIssueDetailsFromComplaint = (complaintData) => {
        const storedSummary = complaintData?.issueSummary?.trim();
        if (storedSummary) return storedSummary;

        if (!complaintData?.reportedBy && complaintData?.levelOneQuestion) {
            return QR_REPAIR_ISSUE_SUMMARY;
        }

        if (!complaintData?.chat || !Array.isArray(complaintData.chat)) return '-';
        const issueMessage = complaintData.chat.find(
            (entry) => entry && entry.from === 'panel' && !entry.isImage && typeof entry.text === 'string' && entry.text.trim().length > 0
        );
        return issueMessage?.text || '-';
    };

    const getIssuePhotoFromComplaint = (complaintData) => {
        if (!complaintData?.chat || !Array.isArray(complaintData.chat)) return null;
        const imageMessage = complaintData.chat.find((entry) => entry && entry.from === 'panel' && entry.isImage && typeof entry.text === 'string' && entry.text.trim().length > 0);
        return imageMessage?.text || null;
    };

    const resolveIssuePhotoUrl = (photoValue) => {
        if (!photoValue || typeof photoValue !== 'string') return null;
        const trimmed = photoValue.trim();
        if (!trimmed) return null;

        // Already absolute (http/https) or embedded (data URI)
        if (trimmed.startsWith('http://') || trimmed.startsWith('https://') || trimmed.startsWith('data:image')) {
            return trimmed;
        }

        const imageBase = process.env.REACT_APP_IMAGE_BASE_URL || process.env.REACT_APP_BACKEND_URL?.replace(/\/api\/v1\/?$/, '/') || `${window.location.origin}/`;
        const normalizedBase = imageBase.endsWith('/') ? imageBase : `${imageBase}/`;
        const normalizedPath = trimmed.startsWith('/') ? trimmed.slice(1) : trimmed;
        return `${normalizedBase}${normalizedPath}`;
    };

    const complaintIssuePhoto = getIssuePhotoFromComplaint(complaint);
    const complaintIssuePhotoUrl = resolveIssuePhotoUrl(complaintIssuePhoto);
    const issueDetails = getIssueDetailsFromComplaint(complaint);
    const customerConversation = useMemo(() => {
        if (!Array.isArray(complaint?.chat)) return [];
        const customerLabel = complaint.customerName || complaint.whatsAppNumber || t('Customer');

        return complaint.chat
            .filter((entry) => {
                if (!entry || isInternalChatEntry(entry)) return false;
                return typeof entry.text === 'string' && entry.text.trim().length > 0;
            })
            .slice()
            .sort((left, right) => new Date(left.createdAt || 0) - new Date(right.createdAt || 0))
            .map((entry, index) => {
                const direction = entry.from === 'system' ? 'system' : entry.from === 'panel' ? 'support' : 'customer';
                return {
                    ...entry,
                    key: entry._id || `${entry.createdAt || 'message'}-${index}`,
                    direction,
                    author:
                        direction === 'system'
                            ? t('System')
                            : direction === 'support'
                            ? formatUserDisplay(entry.createdBy) === '-'
                                ? t('Support')
                                : formatUserDisplay(entry.createdBy)
                            : customerLabel,
                };
            });
        // `t` follows the selected language and therefore belongs in this dependency list.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [complaint, preferredLanguage]);
    const technicalHandoff = useMemo(() => {
        const handoffs = Array.isArray(complaint?.technicalHandoffs) ? complaint.technicalHandoffs : [];
        if (!handoffs.length) return null;

        const orderId = String(order?._id || '');
        const matchingOrder = [...handoffs].reverse().find((handoff) => {
            const handoffOrderId = handoff?.repairOrder?._id || handoff?.repairOrder;
            return orderId && String(handoffOrderId || '') === orderId;
        });

        return matchingOrder || handoffs[handoffs.length - 1];
    }, [complaint?.technicalHandoffs, order?._id]);
    let transferContext = null;
    if (technicalHandoff) {
        transferContext = {
            origin: t('Sent by Customer Support'),
            reason: technicalHandoff.reason || '-',
            sentBy: formatUserDisplay(technicalHandoff.sentBy),
            sentAt: technicalHandoff.sentAt || technicalHandoff.createdAt || null,
        };
    } else if (complaint?.source === 'whatsapp_bot' && order?._id) {
        transferContext = {
            origin: t('Created automatically from WhatsApp'),
            reason: complaint?.levelTwoQuestion?.text || complaint?.levelOneQuestion?.text || issueDetails || '-',
            sentBy: t('System'),
            sentAt: order?.createdAt || complaint?.createdAt || null,
        };
    }

    const sparePartUsageStatusTemplate = (row) => statusTag(row.status);

    const estimateDataUrlSize = (dataUrl) => {
        if (!dataUrl || typeof dataUrl !== 'string') return 0;
        const parts = dataUrl.split(',');
        if (parts.length < 2) return 0;
        return Math.ceil((parts[1].length * 3) / 4);
    };

    const getTotalPhotoBytes = (photos = []) => photos.reduce((sum, photo) => sum + estimateDataUrlSize(photo), 0);

    const addClosePhoto = (photoValue) => {
        if (!photoValue || typeof photoValue !== 'string') return;
        setCloseForm((prev) => {
            if (prev.photoUrls.length >= MAX_CLOSE_PHOTOS) {
                toast.warn(t(`Maximum ${MAX_CLOSE_PHOTOS} photos allowed.`));
                return prev;
            }
            const nextPhotos = [...prev.photoUrls, photoValue];
            const totalBytes = getTotalPhotoBytes(nextPhotos);
            if (totalBytes > MAX_CLOSE_TOTAL_BYTES) {
                toast.warn(t('Total photo size limit reached. Remove one photo or upload smaller images.'));
                return prev;
            }
            return {
                ...prev,
                photoUrls: nextPhotos,
            };
        });
    };

    const openPhotoPreview = (photoUrl) => {
        const resolvedUrl = resolveIssuePhotoUrl(photoUrl);
        if (!resolvedUrl) return;
        setPhotoPreviewUrl(resolvedUrl);
        setPhotoPreviewVisible(true);
    };

    const formatDisplayDate = (value) => formatLocalizedDateTime(value, preferredLanguage);

    const renderResponsiveRows = (columns, rows, emptyMessage) => {
        if (!rows.length) return <p className="repair-detail__empty">{emptyMessage}</p>;
        const visibleColumns = columns.filter((column) => column.render || rows.some((row) => row[column.key] != null && row[column.key] !== '' && row[column.key] !== '-'));
        if (isMobile)
            return (
                <div className="repair-detail__records">
                    {rows.map((row, index) => (
                        <dl className="repair-detail__facts repair-detail__record" key={row.id || index}>
                            {visibleColumns
                                .filter((column) => column.render || (row[column.key] != null && row[column.key] !== '' && row[column.key] !== '-'))
                                .map((column) => (
                                    <div key={column.key}>
                                        <dt>{t(column.label)}</dt>
                                        <dd>{column.render ? column.render(row) : row[column.key]}</dd>
                                    </div>
                                ))}
                        </dl>
                    ))}
                </div>
            );
        return (
            <div className="repair-detail__table-wrap" tabIndex={0} role="region" aria-label={t('Details')}>
                <table className="repair-detail__table">
                    <thead>
                        <tr>
                            {visibleColumns.map((column) => (
                                <th scope="col" key={column.key}>
                                    {t(column.label)}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row, index) => (
                            <tr key={row.id || index}>
                                {visibleColumns.map((column) => (
                                    <td key={column.key}>{column.render ? column.render(row) : row[column.key] ?? '-'}</td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    };

    const assignmentHistoryColumns = [
        { key: 'technician', label: 'Technician' },
        { key: 'claimedBy', label: 'Claimed By' },
        { key: 'claimedAt', label: 'Claimed At' },
        { key: 'source', label: 'Source' },
    ];

    const partsUsedColumns = [
        { key: 'sku', label: 'SKU' },
        { key: 'description', label: 'Description' },
        { key: 'quantity', label: 'Qty' },
        { key: 'technician', label: 'Technician' },
    ];

    const sparePartRequestRows = sparePartUsage.map((row, index) => ({
        id: row._id || index,
        part: `${row.sparePart?.sku || ''} – ${row.sparePart?.description || ''}`.trim() || '-',
        quantity: row.quantity || '-',
        technician: `${row.technician?.firstName || ''} ${row.technician?.lastName || ''}`.trim() || '-',
        reviewedBy: `${row.supervisor?.firstName || ''} ${row.supervisor?.lastName || ''}`.trim() || '-',
        reviewedAt: formatLocalizedDateTime(row.authorizationDate, preferredLanguage),
        reviewComment: row.reviewComment || '-',
        status: row.status,
    }));

    const sparePartRequestColumns = [
        { key: 'part', label: 'Part' },
        { key: 'quantity', label: 'Qty' },
        { key: 'technician', label: 'Technician' },
        { key: 'reviewedBy', label: 'Approved By' },
        { key: 'reviewedAt', label: 'Reviewed At' },
        { key: 'reviewComment', label: 'Review Comment' },
        {
            key: 'status',
            label: 'Status',
            render: (row) => sparePartUsageStatusTemplate(row),
        },
    ];

    const present = (value) => value !== false && value !== null && value !== undefined && value !== '' && value !== '-';
    const facts = (items) => (
        <dl className="repair-detail__facts">
            {items
                .filter((item) => present(item.value))
                .map((item) => (
                    <div key={item.label}>
                        <dt>{t(item.label)}</dt>
                        <dd>{item.value}</dd>
                    </div>
                ))}
        </dl>
    );
    const machine = order?.machine || {};
    const machineLabel = machine.serialNumber || machine.deviceId || machine.uuid;
    const questionPath = [complaint?.levelOneQuestion?.text, complaint?.levelTwoQuestion?.text, complaint?.levelThreeQuestion?.text].filter(Boolean);
    const problem =
        present(issueDetails) && issueDetails !== QR_REPAIR_ISSUE_SUMMARY
            ? issueDetails
            : questionPath[questionPath.length - 1] || t('No issue description was saved. Check the conversation for context.');
    const requirements = [
        ...(!isFollowUpOrder
            ? [
                  {
                      label: 'Type of Failure',
                      complete: Boolean(closeForm.typeOfFailure),
                  },
                  {
                      label: 'Failure Category',
                      complete: Boolean(closeForm.failureCategory),
                  },
              ]
            : []),
        { label: 'Final Status', complete: Boolean(closeForm.finalStatus) },
        {
            label: isFollowUpOrder ? 'Follow-up Notes' : 'Actions Taken',
            complete: Boolean(closeForm.actionsTaken.trim()),
        },
        { label: 'At least one photo', complete: hasAtLeastOnePhoto },
    ];
    const transferComplaintId = complaint?._id || order?.complaint?._id || (typeof order?.complaint === 'string' ? order.complaint : null);
    const canTransfer = Boolean(transferComplaintId) && ['admin', 'superAdmin', 'support', 'technician'].includes(roleUid);

    return (
        <main className="repair-detail" aria-busy={loadingOrder}>
            <Button label={t('Back to List')} icon="pi pi-arrow-left" className="p-button-text repair-detail__back" onClick={() => navigate('/repair-orders')} />
            <header className="repair-detail__header">
                <div>
                    <div className="repair-detail__eyebrow">{isFollowUpOrder ? t('Follow-up') : t('Repair Order Detail')}</div>
                    <h1>{machineLabel ? `${t('Machine')} ${machineLabel}` : t('Repair Order Detail')}</h1>
                    {machine.location && (
                        <p>
                            <i className="pi pi-map-marker" aria-hidden="true" /> {machine.location}
                        </p>
                    )}
                </div>
                {order && (
                    <div className="repair-detail__actions">
                        {canEditReport && <Button label={t('Edit ticket')} icon="pi pi-pencil" onClick={() => {
                            setEditForm({ issueSummary: issueDetails === '-' ? '' : issueDetails, photoUrl: '' });
                            setEditVisible(true);
                        }} />}
                        {canTransfer && <SupportHandoffButton complaintId={transferComplaintId} t={t} onTransferred={() => navigate('/repair-orders')} />}
                        {canClose && <Button label={isFollowUpOrder ? t('Close Follow-up') : t('Close Repair Order')} icon="pi pi-check" onClick={() => setCloseVisible(true)} />}
                    </div>
                )}
            </header>

            {loadingOrder && !order ? (
                <div className="repair-detail__empty" role="status">
                    {t('Loading...')}
                </div>
            ) : orderLoadFailed ? (
                <div className="repair-detail__empty" role="alert">
                    <p>{t('The order could not be loaded.')}</p>
                    <Button label={t('Retry')} onClick={loadOrder} />
                </div>
            ) : (
                order && (
                    <>
                        <section className="repair-detail__summary" aria-label={t('Order summary')}>
                            <div>
                                <span>{t('Status')}</span>
                                {statusTag(order.status)}
                            </div>
                            <div>
                                <span>{t('Assigned Technician')}</span>
                                <strong>{order.technician ? formatUserDisplay(order.technician) : t('Unassigned')}</strong>
                            </div>
                            {order.createdAt && (
                                <div>
                                    <span>{t('Created At')}</span>
                                    <strong>{formatDisplayDate(order.createdAt)}</strong>
                                </div>
                            )}
                            {machine.type && (
                                <div>
                                    <span>{t('Machine Type')}</span>
                                    <strong>
                                        {t(machine.type)}
                                        {machine.model ? ` · ${machine.model}` : ''}
                                    </strong>
                                </div>
                            )}
                        </section>
                        {isOperator && <p className="repair-detail__notice">{t('Operators have read-only access to repair closure details.')}</p>}
                        <TabView activeIndex={activeTab} onTabChange={(event) => setActiveTab(event.index)} className="repair-detail__tabs">
                            <TabPanel header={t('Complaint Details')} leftIcon="pi pi-file mr-2">
                                <div className="repair-detail__overview">
                                    <section className="repair-detail__panel repair-detail__problem">
                                        <div className="repair-detail__section-heading">
                                            <h2>{isFollowUpOrder ? t('Follow-up Details') : t('Issue Details')}</h2>
                                        </div>
                                        <p className="repair-detail__description">{problem}</p>
                                {order.status !== 'completed' && (order.typeOfFailure || order.failureCategory) && facts([
                                    { label: 'Failure Type', value: order.typeOfFailure && t(order.typeOfFailure) },
                                    { label: 'Failure Category', value: order.failureCategory && t(order.failureCategory) },
                                    { label: 'Failure Priority', value: order.failurePriority }
                                ])}

                                        {transferContext && (
                                            <div className="repair-detail__context">
                                                <span>{transferContext.origin}</span>
                                                {present(transferContext.reason) && transferContext.reason !== problem && <p>{transferContext.reason}</p>}
                                            </div>
                                        )}
                                        {complaintIssuePhotoUrl && (
                                            <button
                                                type="button"
                                                className="repair-detail__photo"
                                                onClick={() => openPhotoPreview(complaintIssuePhoto)}
                                                aria-label={t('View issue photo')}
                                            >
                                                <img src={complaintIssuePhotoUrl} alt={t('Issue Photo')} />
                                                <span>
                                                    <i className="pi pi-search-plus" aria-hidden="true" /> {t('View')}
                                                </span>
                                            </button>
                                        )}
                                        {customerConversation.length > 0 && (
                                            <Button
                                                label={`${t('View conversation')} (${customerConversation.length})`}
                                                icon="pi pi-comments"
                                                className="p-button-text"
                                                onClick={() => setActiveTab(1)}
                                            />
                                        )}
                                    </section>
                                    <aside className="repair-detail__panel">
                                        <h2>{t('Customer')}</h2>
                                        {complaint?.customerName || complaint?.whatsAppNumber ? (
                                            facts([
                                                {
                                                    label: 'Customer Name',
                                                    value: complaint.customerName,
                                                },
                                                {
                                                    label: 'WhatsApp Number',
                                                    value: complaint.whatsAppNumber,
                                                },
                                                {
                                                    label: 'Total Complaints from Customer',
                                                    value: complaint.customerComplaintCount,
                                                },
                                            ])
                                        ) : (
                                            <p className="repair-detail__muted">{t('No customer contact was saved.')}</p>
                                        )}
                                        <div className="repair-detail__aside-action">
                                            <h3>{t('Spare Parts')}</h3>
                                            <p>
                                                {sparePartRequestRows.length} {t('requests')} · {partsUsedRows.length} {t('linked parts')}
                                            </p>
                                            <Button label={t('View spare parts')} icon="pi pi-box" className="p-button-outlined p-button-sm" onClick={() => setActiveTab(2)} />
                                        </div>
                                    </aside>
                                </div>
                                {order.status === 'completed' && (
                                    <section className="repair-detail__panel repair-detail__resolution">
                                        <h2>
                                            <i className="pi pi-check-circle" aria-hidden="true" /> {t('Resolution')}
                                        </h2>
                                        {facts([
                                            {
                                                label: 'Final Status',
                                                value: order.finalStatus && t(order.finalStatus),
                                            },
                                            {
                                                label: 'Failure Type',
                                                value: order.typeOfFailure && t(order.typeOfFailure),
                                            },
                                            {
                                                label: 'Failure Category',
                                                value: order.failureCategory && t(order.failureCategory),
                                            },
                                            {
                                                label: 'Failure Priority',
                                                value: order.failurePriority,
                                            },
                                            {
                                                label: 'Closed At',
                                                value: order.closedAt && formatDisplayDate(order.closedAt),
                                            },
                                            {
                                                label: 'Time Consumed',
                                                value: order.timeConsumedMinutes != null && formatTimeFromMinutes(order.timeConsumedMinutes, preferredLanguage),
                                            },
                                        ])}
                                        {order.actionsTaken && <p className="repair-detail__description">{order.actionsTaken}</p>}
                                        <div className="repair-detail__gallery">
                                            {(order.photos || []).map((photo, index) => (
                                                <button
                                                    type="button"
                                                    key={index}
                                                    className="repair-detail__photo"
                                                    onClick={() => openPhotoPreview(photo)}
                                                    aria-label={`${t('Repair Photo')} ${index + 1}`}
                                                >
                                                    <img src={resolveIssuePhotoUrl(photo)} alt={`${t('Repair Photo')} ${index + 1}`} />
                                                </button>
                                            ))}
                                        </div>
                                    </section>
                                )}
                                <details className="repair-detail__details">
                                    <summary>{t('Order data and original report')}</summary>
                                    {facts([
                                        { label: 'Order ID:', value: order._id },
                                        { label: 'Complaint ID', value: complaint?._id },
                                        { label: 'Model', value: machine.model },
                                        {
                                            label: 'Claimed By',
                                            value: order.claimedBy && formatUserDisplay(order.claimedBy),
                                        },
                                        {
                                            label: 'Claimed At',
                                            value: order.claimedAt && formatDisplayDate(order.claimedAt),
                                        },
                                        { label: 'Sent by', value: transferContext?.sentBy },
                                        {
                                            label: 'Sent at',
                                            value: transferContext?.sentAt && formatDisplayDate(transferContext.sentAt),
                                        },
                                    ])}
                                    {questionPath.length > 0 && (
                                        <>
                                            <h3>{t('Customer selections')}</h3>
                                            <ol className="repair-detail__questions">
                                                {questionPath.map((text, index) => (
                                                    <li key={index}>{text}</li>
                                                ))}
                                            </ol>
                                        </>
                                    )}
                                </details>
                            </TabPanel>
                            <TabPanel header={`${t('Conversation')} (${customerConversation.length})`} leftIcon="pi pi-comments mr-2">
                                <section className="repair-detail__panel">
                                    <div className="repair-detail__section-heading">
                                        <h2>{t('Conversation with customer')}</h2>
                                        <span>{t('Read-only context for the assigned technician')}</span>
                                    </div>
                                    <div className="repair-detail__conversation" role="region" aria-label={t('Conversation with customer')} tabIndex={0}>
                                        {customerConversation.length ? (
                                            customerConversation.map((message) => {
                                                const mediaUrl = message.isImage ? resolveIssuePhotoUrl(message.text) : null;
                                                return (
                                                    <article className={`repair-detail__message repair-detail__message--${message.direction}`} key={message.key}>
                                                        <div className="repair-detail__message-meta">
                                                            <strong>{message.author}</strong>
                                                            <span>{formatDisplayDate(message.createdAt)}</span>
                                                        </div>
                                                        <div className="repair-detail__bubble">
                                                            {mediaUrl ? (
                                                                <>
                                                                    <button
                                                                        type="button"
                                                                        className="repair-detail__photo"
                                                                        onClick={() => openPhotoPreview(message.text)}
                                                                        aria-label={t('View chat image')}
                                                                    >
                                                                        <img src={mediaUrl} alt={t('Chat image')} />
                                                                    </button>
                                                                    {message.caption && <p>{message.caption}</p>}
                                                                </>
                                                            ) : (
                                                                message.text
                                                            )}
                                                        </div>
                                                    </article>
                                                );
                                            })
                                        ) : (
                                            <p className="repair-detail__empty">{t('No public conversation was saved for this ticket.')}</p>
                                        )}
                                    </div>
                                </section>
                            </TabPanel>
                            <TabPanel header={`${t('Spare Parts')} (${sparePartRequestRows.length})`} leftIcon="pi pi-box mr-2">
                                <div className="repair-detail__section-heading">
                                    <div>
                                        <h2>{t('Spare Parts')}</h2>
                                        <p>{t('Requests and parts linked to this order, in one place.')}</p>
                                    </div>
                                    {['admin', 'superAdmin'].includes(roleUid) && order?._id && <Button label="Crear solicitud de compra" icon="pi pi-shopping-cart" onClick={() => navigate(`/purchasing?originType=repair_order&originId=${order._id}`)} />}
                                    {!isOperator && <Button label={t('Request Spare Part')} icon="pi pi-plus" onClick={() => setRequestVisible(true)} />}
                                </div>
                                <section className="repair-detail__panel">
                                    <h3>{t('Spare Part Requests for this Ticket')}</h3>
                                    {renderResponsiveRows(sparePartRequestColumns, sparePartRequestRows, t('No spare part requests linked to this complaint yet.'))}
                                </section>
                                {partsUsedRows.length > 0 && (
                                    <section className="repair-detail__panel">
                                        <h3>{t('Spare Parts Used (Linked)')}</h3>
                                        {renderResponsiveRows(partsUsedColumns, partsUsedRows, '')}
                                    </section>
                                )}
                            </TabPanel>
                            <TabPanel header={t('Assignment History')} leftIcon="pi pi-history mr-2">
                                <section className="repair-detail__panel">
                                    <h2>{t('Assignment History')}</h2>
                                    {renderResponsiveRows(assignmentHistoryColumns, assignmentLogRows, t('No assignment history yet.'))}
                                </section>
                            </TabPanel>
                        </TabView>
                    </>
                )
            )}
            <Dialog header={t('Edit ticket')} visible={editVisible} modal className="repair-detail__dialog"
                style={{ width: 'min(94vw, 42rem)' }} closable={!savingEdit}
                onHide={() => { if (!savingEdit) setEditVisible(false); }}>
                <label htmlFor="report-description" className="block mb-2">{t('Issue Details')}</label>
                <InputTextarea id="report-description" rows={4} className="w-full" maxLength={10000}
                    value={editForm.issueSummary} disabled={savingEdit}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, issueSummary: e.target.value }))} />
                <label className="block mt-3 mb-2">{t('Issue Photo')}</label>
                {complaintIssuePhotoUrl && !editForm.photoUrl && <img src={complaintIssuePhotoUrl} alt={t('Issue Photo')} style={{ maxWidth: '100%', maxHeight: '180px' }} />}
                <PhotoUploader forceJpeg onProcessingChange={setProcessingEditPhoto} value={editForm.photoUrl} disabled={savingEdit} maxUploadBytes={500 * 1024} maxDimension={1280}
                    onChange={(photoUrl) => setEditForm((prev) => ({ ...prev, photoUrl }))} />
                <div className="flex justify-content-end gap-2 mt-3">
                    <Button label={t('Cancel')} className="p-button-text" disabled={savingEdit} onClick={() => setEditVisible(false)} />
                    <Button label={t('Save changes')} icon="pi pi-check" loading={savingEdit}
                        disabled={savingEdit || processingEditPhoto || (!editForm.issueSummary.trim() && !editForm.photoUrl)} onClick={handleSaveReport} />
                </div>
            </Dialog>
            <Dialog
                header={isFollowUpOrder ? t('Close Follow-up') : t('Close Repair Order')}
                visible={closeVisible}
                modal
                className="repair-detail__dialog"
                style={{ width: 'min(94vw, 52rem)' }}
                onHide={() => {
                    if (!savingClose) setCloseVisible(false);
                }}
                closable={!savingClose}
            >
                <p id="repair-close-requirements" className="repair-detail__muted">
                    {t('Complete these details to close the order. Your entries stay here if you close this window.')}
                </p>
                <ul className="repair-detail__requirements" aria-label={t('Required details')}>
                    {requirements.map((item) => (
                        <li key={item.label} className={item.complete ? 'is-complete' : ''}>
                            <i className={item.complete ? 'pi pi-check-circle' : 'pi pi-circle'} aria-hidden="true" />
                            <span>
                                {t(item.label)}
                                {item.complete ? ` · ${t('Ready')}` : ` · ${t('Required')}`}
                            </span>
                        </li>
                    ))}
                </ul>
                <div className="grid p-fluid">
                    {!isFollowUpOrder && (
                        <>
                            <div className="col-12 md:col-6">
                                <label htmlFor="repair-failure-type" className="block mb-2">
                                    {t('Type of Failure')} <span style={{ color: 'red' }}>*</span>
                                </label>
                                <Dropdown
                                    inputId="repair-failure-type"
                                    aria-required="true"
                                    value={closeForm.typeOfFailure}
                                    options={localizedFailureTypeOptions}
                                    onChange={(e) =>
                                        setCloseForm((prev) => ({
                                            ...prev,
                                            typeOfFailure: e.value,
                                            failureCategory: '',
                                        }))
                                    }
                                    placeholder={t('Select failure type')}
                                    className="w-full"
                                    disabled={!canClose || savingClose}
                                />
                            </div>
                            <div className="col-12 md:col-6">
                                <label htmlFor="repair-failure-category" className="block mb-2">
                                    {t('Failure Category')} <span style={{ color: 'red' }}>*</span>
                                </label>
                                <Dropdown
                                    inputId="repair-failure-category"
                                    aria-required="true"
                                    value={closeForm.failureCategory}
                                    options={localizedFailureCategoryOptions}
                                    onChange={(e) =>
                                        setCloseForm((prev) => ({
                                            ...prev,
                                            failureCategory: e.value,
                                        }))
                                    }
                                    placeholder={t('Select failure category')}
                                    className="w-full"
                                    filter
                                    disabled={!canClose || savingClose}
                                />
                                {selectedFailurePriority && (
                                    <small className="text-700 block mt-2">
                                        {t('Failure Priority')}: <strong>{selectedFailurePriority}/10</strong>
                                    </small>
                                )}
                            </div>
                        </>
                    )}
                    <div className="col-12 md:col-6">
                        <label htmlFor="repair-final-status" className="block mb-2">
                            {t('Final Status')} <span style={{ color: 'red' }}>*</span>
                        </label>
                        <Dropdown
                            inputId="repair-final-status"
                            aria-required="true"
                            value={closeForm.finalStatus}
                            options={localizedFinalStatusOptions}
                            onChange={(e) =>
                                setCloseForm((prev) => ({
                                    ...prev,
                                    finalStatus: e.value,
                                }))
                            }
                            placeholder={t('Select final status')}
                            className="w-full"
                            disabled={!canClose || savingClose}
                        />
                    </div>
                    <div className="col-12">
                        <label htmlFor="repair-actions" className="block mb-2">
                            {isFollowUpOrder ? t('Follow-up Notes') : t('Actions Taken')} <span style={{ color: 'red' }}>*</span>
                        </label>
                        <InputTextarea
                            id="repair-actions"
                            aria-required="true"
                            rows={4}
                            value={closeForm.actionsTaken}
                            onChange={(e) =>
                                setCloseForm((prev) => ({
                                    ...prev,
                                    actionsTaken: e.target.value,
                                }))
                            }
                            placeholder={isFollowUpOrder ? t('Describe the follow-up visit result') : t('Describe the work performed')}
                            className="w-full"
                            disabled={!canClose || savingClose}
                        />
                    </div>
                    <div className="col-12">
                        <label className="block mb-2">
                            {isFollowUpOrder ? t('Follow-up Photos') : t('Repair Photos')} <span style={{ color: 'red' }}>*</span>
                        </label>
                        <PhotoUploader
                            value=""
                            onChange={addClosePhoto}
                            maxUploadBytes={500 * 1024}
                            maxDimension={1280}
                            helperText={`${MAX_CLOSE_PHOTOS} ${t('photos')}. ${t('Max 500 KB each.')}`}
                            disabled={!canClose || savingClose}
                        />
                        {closeForm.photoUrls.length > 0 && (
                            <div className="mt-3">
                                <small className="text-500 block mb-2">
                                    {closeForm.photoUrls.length} {closeForm.photoUrls.length !== 1 ? t('photos') : t('photo')} {t('added')}
                                </small>
                                <div className="grid">
                                    {closeForm.photoUrls.map((photo, index) => (
                                        <div className={`col-12 ${isMobile ? '' : 'md:col-6'}`} key={`repair-close-photo-${index}`}>
                                            <div
                                                style={{
                                                    border: '1px solid rgba(148,163,184,0.4)',
                                                    borderRadius: '6px',
                                                    padding: '0.5rem',
                                                }}
                                            >
                                                <img
                                                    src={resolveIssuePhotoUrl(photo) || photo}
                                                    alt={`Repair ${index + 1}`}
                                                    style={{
                                                        width: '100%',
                                                        maxHeight: isMobile ? '220px' : '160px',
                                                        objectFit: 'cover',
                                                        borderRadius: '4px',
                                                        cursor: 'zoom-in',
                                                    }}
                                                    title={t('Click to enlarge')}
                                                    onClick={() => openPhotoPreview(photo)}
                                                />
                                                <div className="flex justify-content-end mt-2">
                                                    <Button
                                                        type="button"
                                                        label={t('View')}
                                                        icon="pi pi-search-plus"
                                                        className="p-button-text p-button-sm"
                                                        onClick={() => openPhotoPreview(photo)}
                                                    />
                                                </div>
                                                {canClose && (
                                                    <Button
                                                        type="button"
                                                        label={t('Remove')}
                                                        icon="pi pi-times"
                                                        className="p-button-danger p-button-text p-button-sm mt-2"
                                                        onClick={() =>
                                                            setCloseForm((prev) => ({
                                                                ...prev,
                                                                photoUrls: prev.photoUrls.filter((_, i) => i !== index),
                                                            }))
                                                        }
                                                    />
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                <small className="text-500 block mt-2">{t('Click any photo to enlarge.')}</small>
                            </div>
                        )}
                    </div>
                    <div className="col-12 flex justify-content-end mt-3">
                        <Button
                            label={order?.status === 'completed' ? t('Already Closed') : t('Save & Close')}
                            icon="pi pi-check"
                            aria-describedby="repair-close-requirements"
                            disabled={closeDisabled}
                            loading={savingClose}
                            onClick={handleCloseOrder}
                            style={{
                                width: isMobile ? '100%' : 'auto',
                                minWidth: isMobile ? undefined : '220px',
                            }}
                        />
                    </div>
                </div>
            </Dialog>
            <Dialog
                header={t('Request Spare Part')}
                visible={requestVisible}
                modal
                className="repair-detail__dialog"
                style={{ width: 'min(94vw, 42rem)' }}
                onHide={() => {
                    if (!savingRequest) setRequestVisible(false);
                }}
                closable={!savingRequest}
            >
                <div className="grid p-fluid">
                    <div className="col-12 md:col-6">
                        <label htmlFor="repair-part-category" className="block mb-2">
                            {t('Category')}
                        </label>
                        <Dropdown
                            inputId="repair-part-category"
                            value={requestPartCategory}
                            options={sparePartCategoryOptions}
                            onChange={(e) => {
                                setRequestPartCategory(e.value);
                                setRequestForm((prev) => ({
                                    ...prev,
                                    sparePartId: '',
                                }));
                            }}
                            placeholder={t('Select Category')}
                            className="w-full"
                            showClear
                        />
                    </div>
                    <div className="col-12">
                        <label htmlFor="repair-part" className="block mb-2">
                            {t('Part')}
                        </label>
                        <Dropdown
                            inputId="repair-part"
                            value={requestForm.sparePartId}
                            options={sparePartOptions}
                            onChange={(e) => setRequestForm({ ...requestForm, sparePartId: e.value })}
                            placeholder={t('Select Spare Part')}
                            className="w-full"
                            filter
                            disabled={savingRequest}
                        />
                    </div>
                    <div className="col-12 md:col-6">
                        <label htmlFor="repair-part-quantity" className="block mb-2">
                            {t('Quantity')}
                        </label>
                        <InputNumber
                            inputId="repair-part-quantity"
                            value={requestForm.quantity}
                            onValueChange={(e) => setRequestForm({ ...requestForm, quantity: e.value || 1 })}
                            min={1}
                            max={999}
                            className="w-full"
                        />
                    </div>
                    <div className="col-12">
                        <label htmlFor="repair-part-notes" className="block mb-2">
                            {t('Notes')}
                        </label>
                        <InputTextarea
                            rows={3}
                            id="repair-part-notes"
                            value={requestForm.notes}
                            onChange={(e) => setRequestForm({ ...requestForm, notes: e.target.value })}
                            placeholder={t('Reason / additional info for supervisor')}
                            className="w-full"
                        />
                    </div>
                    <div className="col-12 flex justify-content-end mt-3">
                        <Button
                            label={t('Send Request')}
                            icon="pi pi-send"
                            disabled={!requestForm.sparePartId || !requestForm.quantity || savingRequest}
                            loading={savingRequest}
                            onClick={handleRequestSparePart}
                            style={{
                                width: isMobile ? '100%' : 'auto',
                                minWidth: isMobile ? undefined : '220px',
                            }}
                        />
                    </div>
                </div>
            </Dialog>
            <Dialog header={t('Repair Photo')} visible={photoPreviewVisible} style={{ width: 'min(94vw, 980px)' }} modal onHide={() => setPhotoPreviewVisible(false)}>
                {photoPreviewUrl && <img src={photoPreviewUrl} alt={t('Repair Photo')} style={{ width: '100%', maxHeight: '75vh', objectFit: 'contain' }} />}
            </Dialog>
        </main>
    );
};

export default RepairOrderDetail;
